import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { HardHat, LogIn, Eye, EyeOff, Copy, Check, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet';

export default function DemoLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('testowner1767306243@test.com');
  const [password, setPassword] = useState('Password123!');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);

  // Demo credentials
  const DEMO_EMAIL = 'testowner1767306243@test.com';
  const DEMO_PASSWORD = 'Password123!';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome to the demo!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, type) => {
    const setCopied = type === 'email' ? setCopiedEmail : setCopiedPassword;
    
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast.success('Copied!');
      } else {
        throw new Error('Clipboard API not available');
      }
    } catch (err) {
      // Fallback
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
          toast.success('Copied!');
        } else {
          toast.error('Failed to copy');
        }
      } catch (fallbackErr) {
        toast.error('Failed to copy');
      }
    }
  };

  const fillCredentials = () => {
    setEmail(DEMO_EMAIL);
    setPassword(DEMO_PASSWORD);
  };

  return (
    <>
      {/* SEO - Prevent indexing */}
      <Helmet>
        <meta name="robots" content="noindex, nofollow, noarchive, nosnippet, noimageindex" />
        <meta name="googlebot" content="noindex, nofollow" />
        <meta name="bingbot" content="noindex, nofollow" />
        <title>Demo Login | Corbel CRM</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-6">
          {/* Logo */}
          <div className="text-center">
            <div className="inline-flex items-center gap-3 mb-2">
              <div className="h-12 w-12 bg-orange-500 rounded-sm flex items-center justify-center">
                <HardHat className="h-7 w-7 text-white" />
              </div>
              <span className="text-3xl font-bold text-white">Corbel</span>
            </div>
            <p className="text-slate-400">CRM Demo Access</p>
          </div>

          {/* Demo Notice */}
          <Card className="rounded-sm bg-amber-500/10 border-amber-500/30">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-200 font-medium text-sm">Demo Environment</p>
                  <p className="text-amber-300/80 text-xs mt-1">
                    This is a test environment for evaluation purposes. Data may be reset periodically.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Login Card */}
          <Card className="rounded-sm bg-slate-800 border-slate-700">
            <CardHeader className="text-center pb-4">
              <CardTitle className="text-xl text-white">Demo Login</CardTitle>
              <CardDescription className="text-slate-400">
                Use the credentials below to explore the platform
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Demo Credentials Box */}
              <div className="p-4 bg-slate-700/50 rounded-sm border border-slate-600 space-y-3">
                <p className="text-xs text-slate-400 font-medium uppercase tracking-wide">Demo Credentials</p>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500">Email</p>
                      <p className="text-sm text-slate-200 font-mono">{DEMO_EMAIL}</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(DEMO_EMAIL, 'email')}
                      className="text-slate-400 hover:text-white h-8 w-8 p-0"
                    >
                      {copiedEmail ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500">Password</p>
                      <p className="text-sm text-slate-200 font-mono">{DEMO_PASSWORD}</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(DEMO_PASSWORD, 'password')}
                      className="text-slate-400 hover:text-white h-8 w-8 p-0"
                    >
                      {copiedPassword ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={fillCredentials}
                  className="w-full mt-2 rounded-sm border-slate-500 text-slate-300 hover:bg-slate-600 hover:text-white"
                >
                  Fill Credentials
                </Button>
              </div>

              {/* Login Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="rounded-sm bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                    placeholder="Enter email"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-300">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="rounded-sm bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 pr-10"
                      placeholder="Enter password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-sm bg-orange-500 hover:bg-orange-600 text-white"
                >
                  {loading ? (
                    'Logging in...'
                  ) : (
                    <>
                      <LogIn className="h-4 w-4 mr-2" />
                      Enter Demo
                    </>
                  )}
                </Button>
              </form>

              {/* Links */}
              <div className="text-center pt-2">
                <p className="text-sm text-slate-500">
                  Ready to get started?{' '}
                  <Link to="/register" className="text-orange-400 hover:text-orange-300">
                    Create an account
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Footer */}
          <p className="text-center text-xs text-slate-600">
            This demo page is not indexed by search engines
          </p>
        </div>
      </div>
    </>
  );
}
